import type { ReactElement, ReactNode } from 'react';
import React from 'react';
import { faArrowUpAZ, faArrowUpZA, faRightLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ColumnSort, Header, OnChangeFn, SortingState } from '@tanstack/react-table';
import styles from './sort-column-button.module.scss';

type SortColumnButtonProps<TData> = {
  header: Header<TData, unknown>;
  sorting: SortingState;
  onSort: OnChangeFn<SortingState>;
  children: ReactNode;
};

/**
 * Toggles the asc/desc sorting for the column that "header" represents, clearing any sorting of
 * other columns. If we want to support multi-column sorting in the future, this function will
 * need to be amended.
 */
export default function SortColumnButton<TData extends object>({
  header,
  sorting,
  onSort,
  children
}: SortColumnButtonProps<TData>): ReactElement {
  // The column header will be rendered as a button, unless the column definition explicitly has the
  // enableSorting flag set to false. By default, table columns are expected to be sortable.
  if (!header.column.getCanSort()) return <>{children}</>;

  const columnSort: ColumnSort | undefined = sorting.find((s) => s.id === header.id);
  const isSorted = header.column.getIsSorted();

  return (
    <button
      className={styles.sortButton}
      type="button"
      onClick={(): void => onSort([{ id: header.id, desc: columnSort ? !columnSort.desc : true }])}
    >
      {children}
      <span style={{ float: 'right' }}>
        {isSorted && columnSort?.desc && <FontAwesomeIcon icon={faArrowUpZA} fixedWidth />}
        {isSorted && !columnSort?.desc && <FontAwesomeIcon icon={faArrowUpAZ} fixedWidth />}
        {!isSorted && <FontAwesomeIcon icon={faRightLeft} rotation={90} fixedWidth />}
      </span>
    </button>
  );
}
